<template>
    <div id="create-task-form">
        <div class="status-switcher-wrapper" v-if="currentWorkflow">
            <TicketStatusSwitcher
                :status-list="pipelineStages"
                :current-status="currentStage.name"
                @update-status="handleStageChange"
            />
        </div>
        <div class="task-form">
            <div class="row">
                <div class="input title-input">
                    <div class="label">{{ $t("task.selectWorkflow") }}</div>
                    <div style="width: 100%">
                        <a-select
                            style="width: 100%"
                            class="select-multiple"
                            v-model="form.workflowId"
                            :options="workflowOptions"
                            :placeholder="$t('task.selectWorkflow')"
                            @change="bindData"
                        ></a-select>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="input title-input">
                    <div class="label">{{ $t("task.title") }}</div>
                    <a-input class="classic-new-input" v-model:value="form.title"
                             :placeholder="$t('task.taskNumber')" />
                </div>
                <div class="input priority-input">
                    <div class="label">{{ $t("task.priority") }}</div>
                    <a-select class="classic-new-select" v-model:value="form.priority">
                        <a-select-option value="low">
                            <div style="display: flex; gap: 5px">
                                <LowIcon style="width: 20px; height: 20px; margin-top: 3px" />
                                {{ $t("message.low") }}
                            </div>
                        </a-select-option>
                        <a-select-option value="lowest">
                            <div style="display: flex; gap: 5px">
                                <LowestIcon style="width: 20px; height: 20px; margin-top: 3px" />
                                {{ $t("message.lowest") }}
                            </div>
                        </a-select-option>
                        <a-select-option value="medium">
                            <div style="display: flex; gap: 5px">
                                <MediumIcon style="width: 20px; height: 20px; margin-top: 3px" />
                                {{ $t("message.medium") }}
                            </div>
                        </a-select-option>
                        <a-select-option value="high">
                            <div style="display: flex; gap: 5px">
                                <HighIcon style="width: 20px; height: 20px; margin-top: 3px" />
                                {{ $t("message.high") }}
                            </div>
                        </a-select-option>
                        <a-select-option value="highest">
                            <div style="display: flex; gap: 5px">
                                <HighestIcon style="width: 20px; height: 20px; margin-top: 3px" />
                                {{ $t("message.highest") }}
                            </div>
                        </a-select-option>
                    </a-select>
                </div>
            </div>
            <div class="row">
                <quill-editor
                    style="max-height: 20%; width: 100%"
                    class="quill-editor"
                    :options="editorOption"
                    v-model="form.description"
                    :placeholder="$t('auto.createTaskDesc')"
                />
            </div>
            <div class="row">
                <div class="checklist-data">
                    <div style="display: flex; align-items: center; gap: 20px" v-if="form.checklist.length > 0">
                        <div style="font-weight: bold; font-size: 16px; margin-right: 20px">{{ $t("auto.checklist") }}</div>
                        <div style="width: 200px">
                            <a-progress
                                :stroke-color="{
                                            '0%': 'ec4b1d',
                                            '100%': 'ec4b1d',
                                        }"
                                :percent="
                                            (100 / form.checklist.length) *
                                            form.checklist.filter((c) => c.isCompleted).length
                                        "
                                :show-info="false"
                            />
                        </div>
                        <div style="width: 200px">
                            {{
                                $t("task.completedOfCheck", {
                                    total: form.checklist.length,
                                    completed: form.checklist.filter((c) => c.isCompleted).length,
                                })
                            }}
                        </div>
                    </div>
                    <div style="margin-top: 10px; display: flex; gap: 10px; flex-direction: column">
                        <div
                            style="
                                        display: flex;
                                        align-items: center;
                                        justify-content: flex-start;
                                        user-select: none;
                                    "
                            v-for="(checkbox, index) of form.checklist"
                        >
                            <div style="display: flex; align-items: center; gap: 10px">
                                <a-checkbox class="kanban-checkbox large" v-model="checkbox.isCompleted" />
                                <input
                                    style="
                                                width: 300px;
                                                border: none;
                                                outline: none;
                                                background: #f1f1f1;
                                                border-radius: 4px;
                                                padding: 3px 10px;
                                            "
                                    v-model="checkbox.title"
                                />
                            </div>
                            <div class="material-icons" @click="deleteCheckbox(index)">close</div>
                        </div>
                    </div>
                    <div
                        style="margin-top: 10px; color: #ec4b1d; font-weight: bold; cursor: pointer"
                        @click="addNewCheckBox"
                    >
                        {{ !form.checklist.length ? this.$t("task.newCheckList") : this.$t("task.add") }}
                    </div>
                </div>
            </div>
            <div class="attachments-section">
                <div v-if="attachmentFileVisible" class="row" @click="openFileInput" style="cursor: pointer">
                    <div class="attach-files">
                        <div class="text">
                            <div class="material-icons">attachment</div>
                            <div>{{ $t("mail.attacheFiles") }}</div>
                        </div>
                        <div class="logo">
                            <s3Logo />
                        </div>
                    </div>
                    <input id="fileInput" type="file" style="display: none" @change="uploadFile" />
                </div>
                <div class="attachments-loading-overlay" v-if="attachmentsUploading">
                    <div class="attachments-loading-content">
                        <div class="attachments-spinner"></div>
                        <span>{{ $t("auto.uploadingFile") }}</span>
                    </div>
                </div>
                <div class="row attachments-row" style="display: grid; gap: 20px">
                    <div
                        class="attach-files-visibility"
                        v-for="attachment in form.attachments"
                        :key="attachment.id || attachment._id"
                    >
                        <img
                            :src="
                                attachment.v2Format && attachment.cdnUrl
                                    ? attachment.cdnUrl
                                    : `https://static.supsis.live/${attachment.path + attachment.id}.${attachment.ext}`
                            "
                            :alt="attachment.name"
                        />
                        <div class="image-content">
                            <div class="text">
                                <div v-if="editImageName === attachment.id" class="edit-img-name">
                                    <a-input type="text" v-model:value="attachment.name" />
                                </div>
                                <label v-else>{{ attachment.name }}</label>
                            </div>
                            <div class="description">
                                {{ updatedAtFormated(attachment.createdDate) }}
                                <div class="edit-btn">
                                    <div @click="deleteAttachment(attachment.id)" class="underline">
                                        {{ $t("chatbotv2.delete") }}
                                    </div>
                                    |
                                    <div
                                        class="underline"
                                        v-if="attachmentEditVisible"
                                        @click="editImageNameFunc(attachment.id)"
                                    >
                                        {{ $t("settings.edit") }}
                                    </div>
                                    |
                                    <div class="underline" @click="openImage(attachment._id)">
                                        {{ $t("task.view") }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <a-modal
                            id="task-attachment-image"
                            :visible="taskAttachmentsImgVisible"
                            @cancel="taskAttachmentsImgVisible = false"
                            :footer="false"
                        >
                            <img :src="selectedImage.url" :alt="selectedImage.name" />
                        </a-modal>
                    </div>
                </div>
            </div>
            <div class="user-assigne">
                <div class="input" style="width: 100%">
                    <div class="label">{{ $t("task.assignee") }}</div>
                    <div class="user-assigne-container">
                        <a-select
                            class="select-multiple"
                            v-model:value="form.assigneeIds"
                            mode="multiple"
                            :options="visibilityUsers"
                            :filter-option="filterOption"
                        ></a-select>
                        <div class="assigne-user-options">
                            <div class="assigne-option" @click="openAssigne('owner')">
                                {{ $t("task.owner") }}
                            </div>
                            <div class="assigne-option" @click="openAssigne('collaborators')">
                                {{ $t("task.collaborators") }}
                            </div>
                            <div class="assigne-option" @click="openAssigne('followers')">
                                {{ $t("task.followers") }}
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    class="input user-assigne-owner"
                    style="width: 100%"
                    :style="{ height: assigneOptions['owner'] ? '95px' : '0px' }"
                >
                    <div class="row-bracket"></div>
                    <div class="label">{{ $t("task.owner") }}</div>
                    <div class="user-assigne-container">
                        <a-select
                            class="select-multiple"
                            v-model:value="form.ownerId"
                            :options="visibilityUsers"
                        ></a-select>
                    </div>
                </div>
                <div
                    class="input user-assigne-collaborators"
                    style="width: 100%"
                    :style="{ height: assigneOptions['collaborators'] ? '95px' : '0px' }"
                >
                    <div class="row-bracket"></div>
                    <div class="label">{{ $t("task.collaborators") }}</div>
                    <div class="user-assigne-container">
                        <a-select
                            class="select-multiple"
                            v-model:value="form.collaboratorIds"
                            mode="multiple"
                            :options="visibilityUsers"
                            :filter-option="filterOption"
                        ></a-select>
                    </div>
                </div>
                <div
                    class="input user-assigne-followers"
                    style="width: 100%"
                    :style="{ height: assigneOptions['followers'] ? '95px' : '0px' }"
                >
                    <div class="row-bracket"></div>
                    <div class="label">{{ $t("task.followers") }}</div>
                    <div class="user-assigne-container">
                        <a-select
                            class="select-multiple"
                            v-model:value="form.followerIds"
                            mode="multiple"
                            :options="visibilityUsers"
                            :filter-option="filterOption"
                        ></a-select>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="input">
                    <div class="label">{{ $t("message.startDate") }}</div>
                    <div>
                        <a-date-picker dropdownClassName="task-date-picker" v-model="form.startDate" />
                    </div>
                </div>
                <div class="input">
                    <div class="label">{{ $t("message.endDate") }}</div>
                    <div>
                        <a-date-picker dropdownClassName="task-date-picker" v-model="form.endDate" />
                    </div>
                </div>
            </div>
            <div class="row-bracket"></div>
            <div class="row">
                <div class="checklist-data">
                    <div
                        style="display: flex; align-items: center; gap: 20px"
                        v-if="form.subTasks.filter((c) => c.isCompleted && !c.isExits).length > 0"
                    >
                        <div style="font-weight: bold; font-size: 16px; margin-right: 20px">
                            {{ $t("task.subTasks") }}
                        </div>
                        <div style="width: 200px">
                            <a-progress
                                :stroke-color="{
                                            '0%': 'ec4b1d',
                                            '100%': 'ec4b1d',
                                        }"
                                :percent="
                                            (100 / form.subTasks.length) *
                                            form.subTasks.filter((c) => c.isCompleted).length
                                        "
                                :show-info="false"
                            />
                        </div>
                        <div style="width: 200px">
                            <!--                                    Completed: {{ form.subTasks.filter((c) => c.isCompleted).length }} out of-->
                            <!--                                    {{ form.subTasks.filter((c) => c.isCompleted && !c.isExits).length }}-->
                            {{
                                $t("task.completedOfCheck", {
                                    total: form.subTasks.filter((c) => c.isCompleted && !c.isExits).length,
                                    completed: form.subTasks.filter((c) => c.isCompleted).length,
                                })
                            }}
                        </div>
                    </div>
                    <div
                        style="margin-top: 10px; display: flex; gap: 10px; flex-direction: column"
                        v-if="form.subTasks.length > 0"
                    >
                        <div v-for="(subtask, index) of form.subTasks">
                            <div
                                v-if="!subtask.isExits"
                                style="
                                            display: flex;
                                            align-items: center;
                                            justify-content: space-between;
                                            user-select: none;
                                            gap: 20px;
                                        "
                            >
                                <div style="display: flex; align-items: center; gap: 10px; width: 100%">
                                    <a-checkbox class="kanban-checkbox large" v-model="subtask.completed" />
                                    <a-input
                                        class="classic-new-input"
                                        v-model:value="subtask.title"
                                        :placeholder="$t('task.subtaskTitle')"
                                    />
                                </div>
                                <div style="width: 100%">
                                    <a-select
                                        style="width: 100%"
                                        class="select-multiple"
                                        v-model:value="subtask.assignees"
                                        :options="visibilityUsers"
                                        :placeholder="$t('task.assignee')"
                                    ></a-select>
                                </div>
                                <div class="material-icons" @click="deleteSubtask(index)">close</div>
                            </div>
                            <div
                                style="
                                            display: flex;
                                            align-items: center;
                                            justify-content: space-between;
                                            user-select: none;
                                            gap: 20px;
                                        "
                                v-else
                            >
                                <div style="width: 100%">
                                    <a-select
                                        style="width: 100%"
                                        class="select-multiple"
                                        v-model:value="subtask.taskId"
                                        :options="visibilityUsers"
                                        :placeholder="$t('task.assignee')"
                                    ></a-select>
                                </div>
                                <div class="material-icons" @click="deleteSubtask(index)">close</div>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div
                            style="margin-top: 10px; color: #ec4b1d; font-weight: bold; cursor: pointer"
                            @click="addNewSubtask(false)"
                        >
                            {{ $t("task.addSubtask") }}
                        </div>
                        <!--                                <div-->
                        <!--                                    style="margin-top: 10px; color: #ec4b1d; font-weight: bold; cursor: pointer"-->
                        <!--                                    @click="addNewSubtask(true)"-->
                        <!--                                >-->
                        <!--                                    {{ $t("task.addExistsSubtask") }}-->
                        <!--                                </div>-->
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="add-tags ml-2 mt-4">
                    <div
                        style="
                            margin-top: 10px;
                            color: #ec4b1d;
                            font-weight: bold;
                            cursor: pointer;
                            display: flex;
                            align-items: center;
                        "
                    >
                        {{ $t("message.addTag") }}
                        <a-dropdown>
                            <a-button style="margin-left: 10px; padding: 2px">
                                <span class="material-icons">add</span>
                            </a-button>
                            <a-menu slot="overlay">
                                <a-menu-item v-for="tag in tags" :key="tag._id" @click="selectTag(tag)" style="display: flex; align-items: center;">
                                    <div
                                        class="showColor"
                                        :style="{
                                            backgroundColor: tag.color,
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '5px',
                                            marginRight: '8px'
                                        }">
                                    </div>
                                    <span>{{ tag.name }}</span>
                                </a-menu-item>
                            </a-menu>
                        </a-dropdown>
                    </div>
                    <div v-if="form.tags.length > 0" class="selected-tags">
                        <div v-for="tag in form.tags" :key="tag._id" class="tag-item" :style="getTagStyle(tag.color)">
                            <span>{{ tag.name }}</span>
                            <span class="material-icons close-icon" @click="removeTag(tag._id)">close</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import s3Logo from "./svg/S3.vue";
import { mapActions, mapState } from "vuex";
import axios from "axios";
import { uploadAsset } from "../../api/assetUpload";
import LowIcon from "../svg/task/LowIcon.vue";
import LowestIcon from "../svg/task/LowestIcon.vue";
import MediumIcon from "../svg/task/MediumIcon.vue";
import HighIcon from "../svg/task/HighIcon.vue";
import HighestIcon from "../svg/task/HighestIcon.vue";
import TicketStatusSwitcher from "../../views/Pages/Ticket/components/TicketStatusSwitcher.vue";

export default {
    name: "ChatCreateTaskForm",
    components: {
        TicketStatusSwitcher,
        s3Logo,
        LowIcon,
        LowestIcon,
        MediumIcon,
        HighIcon,
        HighestIcon,
    },
    props: {
        messageId: {
            type: String,
            default: null,
        },
        conversationId: {
            type: String,
            default: null,
        },
        formDefaults: {
            type: Object,
            default: null,
        },
        taskId: {
            type: String,
            default: null,
        },
        visitorId: {
            type: String,
            default: null,
        },
        attachmentFileVisible: {
            type: Boolean,
            default: true,
        },
        attachmentEditVisible: {
            type: Boolean,
            default: true,
        },
        windowType: {
            type: String,
            default: "create-task",
        },
        tableRecord: {
            type: String,
            default: null,
        },
        onTaskCreated: {
            type: Function,
            default: null,
        },
    },
    data() {
        let initialForm = {
            title: "",
            description: null,
            priority: "low",
            checklist: [],
            assigneeIds: [],
            ownerId: null,
            collaboratorIds: [],
            followerIds: [],
            subTasks: [],
            startDate: null,
            endDate: null,
            workflowId: null,
            pipelineStageId: null,
            color: null,
            relations: [],
            attachments: [],
            tags: [],
        };
        if (this.formDefaults) {
            initialForm = {
                ...initialForm,
                ...this.formDefaults,
            };
        }
        return {
            editImageName: null,
            form: initialForm,
            tab: "main",
            assigneOptions: {
                owner: true,
                collaborators: false,
                followers: true,
            },
            editorOption: {
                theme: "snow",
                modules: {
                    toolbar: [
                        ["bold", "italic", "underline", "strike"],
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        [{ color: [] }, { background: [] }],
                        [{ align: [] }],
                        [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
                        ["link"],
                    ],
                },
            },

            checked: true,
            taskAttachmentsImgVisible: false,
            selectedImage: {
                url: "",
                name: "",
            },
            tags: [],
            attachmentsUploading: false,
        };
    },
    computed: {
        ...mapState({
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc.push({
                        value: user._id,
                        label: user.name,
                    });
                    return acc;
                }, []),
            defaultWorkflow: (state) => state.tasks.defaultWorkflow,
            site: (state) => state.site.site,
            workflows: (state) =>
                state.tasks.workflows.reduce((acc, workflow) => {
                    acc[workflow._id] = workflow;
                    return acc;
                }, {}),
            user: (state) => state.auth.user,
            isRoot: (state) => state.auth.user.role.isRoot,
        }),
        options() {
            return [
                {
                    value: "low",
                    label: this.$t("task.low"),
                },
                {
                    value: "medium",
                    label: this.$t("task.medium"),
                },
                {
                    value: "high",
                    label: this.$t("task.high"),
                },
            ];
        },
        visibilityUsers() {
            const selectedWorkflow = this.workflows[this.form.workflowId];
            if (selectedWorkflow && selectedWorkflow.visibility?.users?.length > 0) {
                return this.users.filter((user) => selectedWorkflow.visibility.users.includes(user.value));
            }
            return this.users;
        },
        workflowOptions() {
            return this.getWorkflows();
        },
        currentWorkflow() {
            return this.workflows[this.form.workflowId];
        },
        currentStage() {
            return this.currentWorkflow?.pipeline.find((p) => p._id === this.form.pipelineStageId) || {};
        },
        pipelineStages() {
            if (!this.currentWorkflow) return [];

            return this.currentWorkflow.pipeline.map((stage) => ({
                title: stage.name,
                label: stage.name,
                type: stage.type,
                _id: stage._id,
            }));
        },
    },
    methods: {
        ...mapActions({
            getTaskDetail: "tasks/getTaskDetail",
            attachmentsDelete: "tasks/attachmentsDelete",
            getTags: "tag/getTags",
        }),
        bindData() {
            const selectedWorkflow = this.workflows[this.form.workflowId];
            if (selectedWorkflow) {
                this.form.pipelineStageId = selectedWorkflow.pipeline.find((pipeline) => pipeline.type === "todo")?._id;
            }
        },
        handleStageChange(stageName) {
            const stage = this.currentWorkflow.pipeline.find((p) => p.name === stageName);
            if (stage && stage._id !== this.form.pipelineStageId) {
                this.form.pipelineStageId = stage._id;
            }
        },
        filterOption(input, option) {
            return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        addNewCheckBox() {
            if (
                this.form.checklist[this.form.checklist.length - 1] &&
                !this.form.checklist[this.form.checklist.length - 1].title
            ) {
                this.form.checklist = this.form.checklist.slice(0, this.form.checklist.length - 1);
            } else {
                this.form.checklist = [
                    ...this.form.checklist,
                    {
                        isCompleted: false,
                        title: null,
                    },
                ];
            }
        },
        deleteCheckbox(index) {
            this.form.checklist = this.form.checklist.filter((c, i) => i !== index);
        },
        addNewSubtask(isExits) {
            if (
                this.form.subTasks[this.form.subTasks.length - 1] &&
                (this.form.subTasks[this.form.subTasks.length - 1].isExits
                    ? !this.form.subTasks[this.form.subTasks.length - 1].taskId
                    : !this.form.subTasks[this.form.subTasks.length - 1].title)
            ) {
                this.form.subTasks = this.form.subTasks.slice(0, this.form.subTasks.length - 1);
            } else {
                this.form.subTasks = [
                    ...this.form.subTasks,
                    isExits
                        ? {
                            isExits,
                            taskId: null,
                        }
                        : {
                            completed: false,
                            assignees: this.users[0]?.value,
                            title: null,
                        },
                ];
            }
        },
        deleteSubtask(index) {
            this.form.subTasks = this.form.subTasks.filter((c, i) => i !== index);
        },
        openAssigne(key) {
            this.assigneOptions[key] = !this.assigneOptions[key];
        },
        openFileInput() {
            document.getElementById("fileInput").click();
        },
        beforeUpload(file) {
            const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png";
            if (!isJpgOrPng) {
                this.$toasted.show("Sadece Resim Yükleyebilirsiniz!", {
                    duration: 3000,
                });
            }
            const isLt5M = file.size / 1024 / 1024 < 50;
            if (!isLt5M) {
                this.$toasted.show("Resminizin Boyutu En Fazla 5MB Olmalıdır!", {
                    duration: 3000,
                });
            }
            return isJpgOrPng && isLt5M;
        },
        async uploadFile(e) {
            const input = document.getElementById(e.target.id);
            if (!input || !input.files || !input.files[0]) {
                return;
            }
            const file = input.files[0];
            if (!this.beforeUpload(file)) return;
            this.attachmentsUploading = true;
            let path = `${this.site._id}/tasks-attachments/`;
            try {
                let imdUploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "task-attachment",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });

                await axios({
                    method: imdUploadData.data.method,
                    url: imdUploadData.data.url,
                    data: file,
                    headers: { "Content-Type": "application/octet-stream" },
                });
                this.$toasted.success(this.$t("message.successImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
                this.form.attachments = [...this.form.attachments, imdUploadData.data.asset];
            } catch (e) {
                this.$toasted.error(this.$t("message.errorImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
            } finally {
                this.attachmentsUploading = false;
                if (input) {
                    input.value = "";
                }
            }
        },
        updatedAtFormated(a) {
            const date = this.$moment(a).format("DD.MM.YYYY HH:mm");
            return date;
        },
        async deleteAttachment(id) {
            this.form.attachments = this.form.attachments.filter((c) => c.id !== id);
            if (this.details?.taskId) {
                this.attachmentsDelete({ taskId: this.details?.taskId, attachmentId: id });
            } else {
                this.attachmentsDelete({ attachmentId: id });
            }
        },
        editImageNameFunc(id) {
            this.editImageName = this.editImageName !== id && id;
        },
        openImage(id) {
            const image = this.form.attachments.find((attachment) => attachment._id === id);

            if (image) {
                this.selectedImage = {
                    url: image.v2Format && image.cdnUrl
                        ? image.cdnUrl
                        : `https://static.supsis.live/${image.path + image._id}.${image.ext}`,
                    name: image.name,
                };
                this.taskAttachmentsImgVisible = true;
            }
        },
        getWorkflows() {
            return Object.keys(this.workflows)
                .map((workflow) => this.workflows[workflow])
                .filter((workflow) => {
                    if (this.isRoot) {
                        return true;
                    }
                    const visibility = workflow.visibility || {};
                    const users = visibility.users || [];
                    const departments = visibility.departments || [];
                    const userSubjects = this.user.subjects || [];
                    const userId = this.user._id || null;
                    if (users.includes(userId) && departments.length === 0) {
                        return true;
                    } else if (departments.some((dept) => userSubjects.includes(dept)) && users.length === 0) {
                        return true;
                    } else if (users.includes(userId) || departments.some((dept) => userSubjects.includes(dept))) {
                        return true;
                    } else if (users.length === 0 && departments.length === 0) {
                        return true;
                    }
                    return false;
                })
                .map((workflow) => ({
                    value: workflow._id,
                    label: workflow.title,
                }));
        },
        setDefaultWorkflow() {
            if (this.defaultWorkflow) {
                const workflows = this.getWorkflows();
                // Önce varsayılan iş akışını kontrol et
                const hasDefaultWorkflow = workflows.find(w => w.value === this.defaultWorkflow._id);

                if (hasDefaultWorkflow) {
                    this.form.workflowId = this.defaultWorkflow._id;
                } else if (workflows.length > 0) {
                    // Eğer varsayılan iş akışına erişim yoksa, listedeki ilk iş akışını seç
                    this.form.workflowId = workflows[0].value;
                }

                // Pipeline stage'i ayarla
                this.bindData();
            }
        },
        getTagStyle(color) {
            const isLight = this.isLightColor(color);
            return {
                backgroundColor: color,
                color: isLight ? "#000" : "#fff",
            };
        },
        isLightColor(color) {
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);
            const brightness = (r * 299 + g * 587 + b * 114) / 1000;
            return brightness > 155;
        },
        selectTag(tag) {
            const isExists = this.form.tags.find((selectedTag) => selectedTag._id === tag._id);
            if (isExists) {
                this.form.tags = this.form.tags.filter((selectedTag) => selectedTag._id !== tag._id);
            } else {
                this.form.tags = _.uniq([...this.form.tags, tag]);
            }
        },
        removeTag(tagId) {
            this.form.tags = this.form.tags.filter((tag) => tag._id !== tagId);
        },
        async getTaskTags() {
            try {
                this.tags = await this.getTags("task");
            } catch (error) {
                console.error("Error fetching tags:", error);
            }
        },
    },
    mounted() {
        this.setDefaultWorkflow();

        if (!this.taskId && this.user && this.user._id) {
            this.form.ownerId = this.user._id;
            this.form.followerIds = [this.user._id];
            
            this.assigneOptions.owner = true;
            this.assigneOptions.followers = true;
        }

        if (this.taskId) {
            this.getTaskDetail(this.taskId).then((data) => {
                this.form = data;
            });
        }
        if (this.visitorId) {
            this.form.relations.push({ type: "visitor", visitor: this.visitorId });
        }
        if (this.tableRecord) {
            this.form.relations.push({ type: "tableRecord", tableRecord: this.tableRecord });
        }
        if (this.messageId) {
            this.form.relations.push({ type: "message", message: this.messageId });
        }
        if (this.conversationId) {
            this.form.relations.push({ type: "conversation", message: this.conversationId });
        }
        this.getTaskTags();
    },
    watch: {
        "form.workflowId": {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.bindData();
                }
            },
        },
        form: {
            deep: true,
            handler() {
                const workflow = this.workflows[this.form.workflowId];
                const permissions = ["createTask", "editTask", "draggingTask", "deleteTask"].map(
                    (permission) => workflow.permissions[permission],
                );
                const perm = permissions.every((permission) => permission.length === 0);
                const userId = this.user._id;

                if (perm || permissions[0].includes(userId)) {
                    this.$emit("update-form", {
                        ...this.form,
                        attachments: this.details?.taskId
                            ? this.form.attachments
                            : this.form.attachments.map((attachment) => attachment.id),
                    });
                } else {
                    this.$notification.error({
                        message: this.$t("task.permissionError"),
                        description: this.$t("task.permissionErrorDesc"),
                    });
                }
            },
        },
        defaultWorkflow: {
            deep: true,
            handler() {
                this.bindData();
            },
        },
    },
};
</script>
<style lang="scss">
.task-date-picker {
    z-index: 999999;
}

#create-task-form {
    .ant-tabs-ink-bar {
        background-color: #ec4b1d;
    }

    .ant-tabs-nav .ant-tabs-tab-active,
    .ant-tabs-nav .ant-tabs-tab:hover {
        color: #ec4b1d;
    }

    .task-form {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .row {
        flex-wrap: nowrap;
        margin-right: 0px;
        margin-left: 0px;
        gap: 10px;
    }

    .row-bracket {
        width: 100%;
        border-bottom: 1px solid #ec4a1d2e;
        margin: 15px 0px;
    }

    .user-assigne {
        display: flex;
        align-items: center;
        flex-direction: column;
        gap: 10px;

        .select-multiple {
            width: 50%;
        }

        .user-assigne-container {
            display: flex;
            align-items: center;
            gap: 20px;
        }

        .assigne-user-options {
            display: flex;
            align-items: center;
            gap: 20px;

            .assigne-option {
                font-size: 16px;
                font-weight: bold;
                border-bottom: 1px dashed #ec4b1d;
                color: #ec4b1d;
            }
        }
    }

    .classic-new-input {
        padding: 20px;
        background: #f1f1f1;
        border: none;
        color: #3c3c3c;
        font-weight: bold;
        font-size: 14px;

        &:focus {
            border: #ec4a1d65 !important;
            box-shadow: 0 0 0 2px #ffaa0037;
        }

        &::placeholder {
            opacity: 1;
            font-weight: bold;
        }
    }

    .classic-new-select {
        width: 100%;
        font-weight: bold;

        [role="combobox"] {
            padding: 4px;
            border: none !important;
            border-radius: 4px !important;
            background-color: #f1f1f1 !important;
        }
    }

    .user-assigne {
        &-owner,
        &-collaborators,
        &-followers {
            overflow: hidden;
            transition: all 0.4s ease;
            height: 0px;
        }
    }

    .input {
        font-weight: bold;

        .label {
            font-size: 16px;
            margin-bottom: 4px;
        }

        &.title-input {
            width: calc(80% - 10px);
        }

        &.priority-input {
            width: 20%;
        }
    }

    .quill-editor {
        .ql-toolbar.ql-snow {
            background: #f1f1f1;
            border-radius: 4px 4px 0px 0px;
            border: none;
        }

        .ql-container.ql-snow {
            height: 200px;
            max-height: 200px;
            min-height: 200px;
            background: #f1f1f1;
            border-radius: 0px 0px 4px 4px;
            overflow: hidden;
            border: none !important;
        }
    }

    .select-multiple .ant-select-selection {
        border: none !important;
        padding: 5px;
    }

    .checklist-data {
        width: 100%;

        .ant-progress-success-bg,
        .ant-progress-bg {
            background-color: #ec4b1d !important;
        }

        .kanban-checkbox {
            .ant-checkbox-inner {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 20px;
                height: 20px;

                &::after {
                    border: 2px solid #fff;
                    left: 20%;
                    width: 7px;
                    height: 11px;
                    border-top: 0;
                    border-left: 0;
                }
            }

            .ant-checkbox.ant-checkbox-checked {
                .ant-checkbox-inner {
                    border: 3px solid #ec4b1d !important;
                    background-color: #ec4b1d !important;
                }
            }

            .ant-checkbox-checked::after {
                border: 1px solid #ec4b1d !important;
            }

            .ant-checkbox-wrapper:hover .ant-checkbox-inner,
            .ant-checkbox:hover .ant-checkbox-inner,
            .ant-checkbox-input:focus + .ant-checkbox-inner {
                border: 1px solid #ec4b1d !important;
            }
        }
    }

    .attach-files {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 240px;
        border: 1px solid #f1f1f1;
        padding: 3px 20px;
        border-radius: 10px;
        margin-top: 15px;
        font-weight: bold;

        .text {
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .logo svg {
            width: 40px;
        }
    }

    .attachments-section {
        position: relative;
        padding-bottom: 20px;
    }

    .attachments-row {
        position: relative;
        z-index: 1;
    }

    .attachments-loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.9);
        z-index: 2;
    }

    .attachments-loading-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 16px 24px;
        border-radius: 10px;
        background: #fff;
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
        font-weight: 600;
        color: #ec4b1d;
    }

    .attachments-spinner {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 3px solid #ffe0d6;
        border-top-color: #ec4b1d;
        animation: spin 1s linear infinite;
    }

    .attach-files-visibility {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-top: 15px;

        .image-content {
            display: inline-grid;
            align-items: center;
            gap: 5px;

            .text {
                display: flex;
                align-items: center;
                gap: 10px;

                label {
                    font-size: 12px;
                    font-weight: bold;
                    color: #333333;
                }
            }

            .description {
                cursor: pointer;
                font-size: 12px;
                color: #333333;

                .edit-btn {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    color: #ec4b1d;
                    font-weight: bold;
                    font-size: 12px;
                    cursor: pointer;

                    .underline {
                        text-decoration: underline;
                    }
                }
            }
        }

        img {
            width: 100px;
            height: auto;
        }

        .text {
            display: flex;
            align-items: center;
            gap: 5px;
        }

        label {
            margin-bottom: 0px;
        }
    }

    .add-tags {
        width: 100%;
        
        .selected-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            margin-top: 10px;
        }

        .tag-item {
            display: flex;
            align-items: center;
            padding: 5px 10px;
            border-radius: 20px;
            color: #fff;
            font-weight: bold;
        }

        .close-icon {
            margin-left: 10px;
            cursor: pointer;
        }
    }
}

.status-switcher-wrapper {
    margin-bottom: 20px;
    display: flex;
    justify-content: flex-start;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>
